// 마우스 툴팁(PC). 버튼·아이템에 마우스를 올리면 작은 설명 상자가 뜬다(터치·펜은 쓰지 않는다: 터치에는 "올림"이 없다).
// 글은 요소의 data-tip → title → (버튼 역할 요소의) aria-label 순으로 읽는다. title은 처음 올릴 때 data-tip으로 옮겨 브라우저 기본 툴팁과 겹치지 않게 한다.
// 줄바꿈 \n으로 이름과 설명을 나누고, 끝의 「(키)」는 키캡 모양으로 따로 보인다. 상자는 입력을 받지 않고(pointer-events:none), 화면 밖으로 나가지 않게 가장자리에 맞춘다.
// 순수 부분(tipParts·placeTip·placeCombatTip)은 DOM 없이 시험한다(tooltip.test.ts).
import { C, FONT } from './dom';

export interface TipParts { readonly title: string; readonly body: string; readonly key: string }

/** 'Esc 설명\n둘째 줄 (G)' 같은 글을 이름·설명·키로 나눈다. 끝의 「(키)」는 20자 이하의 짧은 토막일 때만 키로 본다 */
export function tipParts(raw: string): TipParts {
  const text = raw.trim();
  const nl = text.indexOf('\n');
  let head = nl < 0 ? text : text.slice(0, nl);
  const body = nl < 0 ? '' : text.slice(nl + 1).trim();
  let key = '';
  const m = /^(.*?)\s*\(([^()]{1,20})\)$/.exec(head);
  if (m && m[1]) { head = m[1]; key = m[2]; }
  return { title: head, body, key };
}

export interface TipPlace { readonly x: number; readonly y: number }
/**
 * 상자 자리: 대상 아래 가운데에 놓고, 아래가 모자라면 위로 뒤집고, 좌우는 화면 안(margin)으로 민다.
 * target = 대상 사각형(화면 px), size = 상자 크기, screen = 화면 크기
 */
export function placeTip(target: { left: number; top: number; width: number; height: number }, size: { w: number; h: number }, screen: { w: number; h: number }, gap = 8, margin = 6): TipPlace {
  let x = target.left + target.width / 2 - size.w / 2;
  x = Math.max(margin, Math.min(screen.w - size.w - margin, x));
  let y = target.top + target.height + gap;
  if (y + size.h + margin > screen.h) y = target.top - size.h - gap;
  y = Math.max(margin, Math.min(screen.h - size.h - margin, y));
  return { x: Math.round(x), y: Math.round(y) };
}

/** 전투 조작의 hover 설명은 오른쪽 1/3 띠에 둔다. 크기는 표시 전에 해당 띠·화면 높이로 제한한다. */
export function placeCombatTip(target: { left: number; top: number; width: number; height: number }, size: { w: number; h: number }, screen: { w: number; h: number }, gap = 8, margin = 6): TipPlace {
  const { y } = placeTip(target, size, screen, gap, margin);
  return { x: Math.floor(screen.w - size.w - margin), y };
}

const SHOW_DELAY = 280;
const TIP_SELECTOR = '[data-tip],[title],button[aria-label],[role="button"][aria-label]';

/** title에서 옮겨 온 사본임을 표시하는 속성(코드가 직접 쓴 data-tip과 구분한다) */
export const TIP_FROM_TITLE = 'data-tip-from-title';

/**
 * 요소의 툴팁 글(없으면 ''). title은 브라우저 기본 툴팁과 겹치지 않게 data-tip으로 옮기되 「title에서 옮겼다」는 표시를 남긴다.
 * 표시가 있는 사본은 캐시일 뿐이라, 같은 요소에 title이 다시 설정되면(예: 경험치 설명 갱신) 새 글로 바꾼다. 직접 쓴 data-tip은 그대로 우선한다
 */
export function tipTextOf(e: Element): string {
  const own = e.getAttribute('data-tip');
  const title = e.getAttribute('title');
  if (own && e.hasAttribute(TIP_FROM_TITLE)) {
    if (title === null) return own;
    e.removeAttribute('title');
    if (title) { e.setAttribute('data-tip', title); return title; }
    // title을 빈 값으로 지웠다 = 설명 없음
    e.removeAttribute('data-tip'); e.removeAttribute(TIP_FROM_TITLE);
    return e.getAttribute('aria-label') ?? '';
  }
  if (own) return own;
  if (title) { e.setAttribute('data-tip', title); e.setAttribute(TIP_FROM_TITLE, ''); e.removeAttribute('title'); return title; }
  return e.getAttribute('aria-label') ?? '';
}

let installed = false;
/** 문서 전체에 한 번만 건다. parent = 상자를 붙일 층(게임 루트) */
export function installTooltips(parent: HTMLElement): void {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  const box = document.createElement('div');
  box.dataset.testid = 'tooltip';
  box.setAttribute('role', 'tooltip');
  box.style.cssText = 'position:absolute;left:0;top:0;z-index:95;display:none;pointer-events:none;max-width:260px;box-sizing:border-box;padding:7px 11px;border-radius:9px;' +
    'border:1.5px solid #d9a441;background:linear-gradient(180deg,rgba(26,34,62,.97),rgba(12,17,36,.97));box-shadow:0 6px 18px rgba(0,0,0,.6);color:#e6ecff;font:600 13px/1.4 ' + FONT + ';';
  parent.appendChild(box);
  const head = document.createElement('div');
  head.style.cssText = 'display:flex;align-items:center;gap:8px;font-weight:800;color:#fff;';
  const titleEl = document.createElement('span');
  const keyEl = document.createElement('span');
  keyEl.style.cssText = 'flex:0 0 auto;min-width:18px;padding:0 6px;border-radius:5px;border:1.5px solid ' + C.gold + ';background:linear-gradient(180deg,#26335a,#10162a);color:' + C.gold + ';font:800 12px ' + FONT + ';text-align:center;';
  head.append(titleEl, keyEl);
  const bodyEl = document.createElement('div');
  bodyEl.style.cssText = 'margin-top:3px;color:#c9d4f5;font-weight:500;white-space:pre-line;';
  box.append(head, bodyEl);

  let timer = 0;
  let cur: Element | null = null;
  const hide = (): void => { window.clearTimeout(timer); timer = 0; cur = null; box.style.display = 'none'; };
  const show = (target: Element): void => {
    const raw = tipTextOf(target);
    if (!raw) return;
    const t = tipParts(raw);
    titleEl.textContent = t.title;
    keyEl.textContent = t.key;
    keyEl.style.display = t.key ? '' : 'none';
    bodyEl.textContent = t.body;
    bodyEl.style.display = t.body ? '' : 'none';
    const r = target.getBoundingClientRect();
    const pr = parent.getBoundingClientRect();
    const combat = target.closest('[data-testid="skillbar"],[data-testid^="touch-"]') !== null;
    box.style.maxWidth = (combat ? Math.min(260, pr.width / 3 - 12) : 260) + 'px';
    box.style.maxHeight = combat ? Math.max(0, pr.height - 12) + 'px' : '';
    box.style.overflowY = combat ? 'auto' : '';
    box.style.overflowWrap = combat ? 'anywhere' : '';
    box.style.display = 'block';
    const place = combat ? placeCombatTip : placeTip;
    const p = place({ left: r.left - pr.left, top: r.top - pr.top, width: r.width, height: r.height }, { w: box.offsetWidth, h: box.offsetHeight }, { w: pr.width, h: pr.height });
    box.style.left = p.x + 'px';
    box.style.top = p.y + 'px';
  };
  document.addEventListener('pointerover', (e) => {
    if (e.pointerType !== 'mouse') return;
    const target = (e.target as Element | null)?.closest?.(TIP_SELECTOR) ?? null;
    if (target === cur) return;
    hide();
    if (!target || !tipTextOf(target)) return;
    cur = target;
    timer = window.setTimeout(() => { if (cur) show(cur); }, SHOW_DELAY);
  });
  document.addEventListener('pointerout', (e) => {
    if (e.pointerType !== 'mouse') return;
    const to = e.relatedTarget as Element | null;
    if (cur && (!to || !cur.contains(to))) hide();
  });
  document.addEventListener('pointerdown', hide, true);
  document.addEventListener('wheel', hide, { passive: true, capture: true });
  window.addEventListener('blur', hide);
  document.addEventListener('keydown', hide, true);
}
